// src/pages/public/FAQ.jsx
import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Search, 
  ChevronDown, 
  HelpCircle,
  DollarSign,
  Shield,
  TrendingUp,
  Download,
  User
} from 'lucide-react'

const FAQ = () => {
  const [activeCategory, setActiveCategory] = useState('general')
  const [openItems, setOpenItems] = useState({})
  const [searchTerm, setSearchTerm] = useState('')

  const categories = [
    { id: 'general', name: 'General', icon: HelpCircle, color: 'blue' },
    { id: 'investment', name: 'Investment', icon: TrendingUp, color: 'green' },
    { id: 'security', name: 'Security', icon: Shield, color: 'purple' },
    { id: 'withdrawal', name: 'Withdrawal', icon: DollarSign, color: 'orange' },
    { id: 'account', name: 'Account', icon: User, color: 'red' },
    { id: 'technical', name: 'Technical', icon: Download, color: 'indigo' }
  ]

  const faqs = {
    general: [
      {
        question: "What is YesTimeEarn?",
        answer: "YesTimeEarn is a modern investment platform that uses AI-powered technology to provide optimal returns on investments. We offer various investment packages with daily returns, instant withdrawals, and institutional-grade security."
      },
      {
        question: "Is YesTimeEarn regulated?",
        answer: "Yes, we operate under strict financial regulations and maintain full compliance with international standards. Our platform is regularly audited and we maintain transparent operations for all our users."
      },
      {
        question: "How do I get started?",
        answer: "Getting started is simple: 1) Create an account, 2) Verify your identity, 3) Choose an investment package, 4) Make your first deposit, 5) Start earning daily returns. The entire process takes less than 5 minutes."
      },
      {
        question: "What countries do you support?",
        answer: "We support users from most countries worldwide. However, due to regulatory requirements, some countries may have restrictions. Please check our terms of service for the complete list of supported countries."
      }
    ],
    investment: [
      {
        question: "What is the minimum investment amount?",
        answer: "We offer investment packages starting from as low as $50, making it accessible for investors at all levels. Each package has different minimum requirements and return rates."
      },
      {
        question: "How are returns calculated?",
        answer: "Returns are calculated daily based on your investment amount and the package's daily ROI rate. Earnings are compounded automatically and can be withdrawn or reinvested according to your preference."
      },
      {
        question: "Can I invest in multiple packages?",
        answer: "Yes, you can invest in multiple packages simultaneously. Many of our users diversify their investments across different packages to optimize returns and manage risk."
      },
      {
        question: "What is the maximum investment amount?",
        answer: "While we don't have a strict maximum limit, larger investments may require additional verification. Our premium packages can accommodate investments up to $500,000 with custom terms available for institutional investors."
      }
    ],
    security: [
      {
        question: "How is my money protected?",
        answer: "We employ multiple security layers including: 256-bit SSL encryption, cold wallet storage for digital assets, insurance protection, 2FA authentication, and regular security audits by third-party firms."
      },
      {
        question: "Is my personal information safe?",
        answer: "Absolutely. We follow strict data protection regulations (GDPR compliant) and never share your personal information with third parties without your consent. All data is encrypted and stored securely."
      },
      {
        question: "What happens if the platform gets hacked?",
        answer: "We have insurance coverage that protects user funds in the unlikely event of a security breach. Additionally, 95% of user funds are stored in cold wallets that are completely offline and inaccessible to hackers."
      }
    ],
    withdrawal: [
      {
        question: "How quickly can I withdraw my earnings?",
        answer: "Withdrawals are processed instantly in most cases. Our automated system typically processes requests within 30 seconds to 2 minutes, depending on network congestion."
      },
      {
        question: "Are there any withdrawal fees?",
        answer: "We charge minimal network fees for cryptocurrency withdrawals. For bank transfers, standard processing fees may apply. There are no hidden fees, and all charges are transparently displayed before confirmation."
      },
      {
        question: "What is the minimum withdrawal amount?",
        answer: "The minimum withdrawal amount is $10 for most payment methods. For cryptocurrency withdrawals, the minimum may vary based on current network conditions."
      }
    ],
    account: [
      {
        question: "How do I verify my account?",
        answer: "Account verification requires a government-issued ID and proof of address. The process is fully automated and typically takes 2-24 hours. You'll receive email notifications throughout the verification process."
      },
      {
        question: "Can I change my investment package?",
        answer: "You can invest in new packages at any time. Existing investments continue according to their original terms. You cannot transfer an active investment to a different package."
      },
      {
        question: "What if I forget my password?",
        answer: "You can reset your password using the 'Forgot Password' feature on the login page. A reset link will be sent to your registered email address."
      }
    ],
    technical: [
      {
        question: "Do you have a mobile app?",
        answer: "Yes, we have mobile apps for both iOS and Android. You can download them from our website or the official app stores. The mobile app provides full functionality including investments, withdrawals, and portfolio tracking."
      },
      {
        question: "What browsers are supported?",
        answer: "We support all modern browsers including Chrome, Firefox, Safari, Edge, and Opera. For the best experience, we recommend using the latest version of your browser."
      },
      {
        question: "How do I enable two-factor authentication?",
        answer: "You can enable 2FA in your account security settings. We support both Google Authenticator and Authy. Once enabled, you'll need to enter a verification code along with your password when logging in."
      }
    ]
  }

  const toggleItem = (category, index) => {
    setOpenItems(prev => ({
      ...prev,
      [`${category}-${index}`]: !prev[`${category}-${index}`]
    }))
  }

  const filteredFAQs = faqs[activeCategory]?.filter(faq => 
    faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-900 dark:to-blue-900/20 pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto"
          >
            <h1 className="text-5xl lg:text-6xl font-bold mb-6">
              Frequently Asked{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-400">
                Questions
              </span>
            </h1>
            <p className="text-xl text-blue-100 mb-8 leading-relaxed">
              Find quick answers to common questions about YesTimeEarn platform, 
              investments, security, and more.
            </p>

            {/* Search Bar */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="max-w-2xl mx-auto relative"
            >
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search FAQs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-white/50"
              />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Categories Sidebar */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:w-1/4"
            >
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700 sticky top-24">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                  Categories
                </h3>
                <div className="space-y-2">
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => setActiveCategory(category.id)}
                      className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all ${
                        activeCategory === category.id
                          ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      <category.icon className="w-5 h-5" />
                      <span className="font-medium">{category.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* FAQ Items */}
            <motion.div
              key={activeCategory}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              className="lg:w-3/4"
            >
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg border border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-3 mb-8">
                  {categories.find(cat => cat.id === activeCategory) && (
                    <>
                      <div className={`bg-${categories.find(cat => cat.id === activeCategory).color}-100 dark:bg-${categories.find(cat => cat.id === activeCategory).color}-900/20 p-3 rounded-2xl`}>
                        {React.createElement(categories.find(cat => cat.id === activeCategory).icon, {
                          className: `w-6 h-6 text-${categories.find(cat => cat.id === activeCategory).color}-600 dark:text-${categories.find(cat => cat.id === activeCategory).color}-400`
                        })}
                      </div>
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                        {categories.find(cat => cat.id === activeCategory).name} Questions
                      </h2>
                    </>
                  )}
                </div>

                <div className="space-y-4">
                  {filteredFAQs?.length > 0 ? (
                    filteredFAQs.map((faq, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="border border-gray-200 dark:border-gray-700 rounded-2xl overflow-hidden"
                      >
                        <button
                          onClick={() => toggleItem(activeCategory, index)}
                          className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                        >
                          <span className="font-semibold text-gray-900 dark:text-white pr-4">
                            {faq.question}
                          </span>
                          <ChevronDown
                            className={`w-5 h-5 text-gray-400 transition-transform ${
                              openItems[`${activeCategory}-${index}`] ? 'rotate-180' : ''
                            }`}
                          />
                        </button>
                        
                        <AnimatePresence>
                          {openItems[`${activeCategory}-${index}`] && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.3 }}
                              className="overflow-hidden"
                            >
                              <div className="p-6 pt-0">
                                <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                                  {faq.answer}
                                </p>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    ))
                  ) : (
                    <div className="text-center py-12">
                      <HelpCircle className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        No questions found
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400">
                        Try adjusting your search terms or browse different categories.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Still Have Questions */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="mt-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl p-8 text-white text-center"
              >
                <h3 className="text-2xl font-bold mb-4">
                  Still have questions?
                </h3>
                <p className="text-blue-100 mb-6">
                  Can't find the answer you're looking for? Please chat with our friendly team.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a
                    href="/contact"
                    className="bg-white text-blue-600 hover:bg-gray-100 px-6 py-3 rounded-xl font-semibold transition-all transform hover:scale-105"
                  >
                    Contact Support
                  </a>
                  <a
                    href="/feedback"
                    className="border-2 border-white text-white hover:bg-white/10 px-6 py-3 rounded-xl font-semibold transition-all"
                  >
                    Submit Feedback
                  </a>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default FAQ